import React, { useCallback, useMemo } from 'react';
import { Controller, type Control } from 'react-hook-form';
import { FormControlLabel, Radio, Stack, Typography } from '@planne-software/uni/mui/material';
import { Input, ListItem, Flag, Select, type SelectOption } from '@planne-software/uni';
import { uni } from '@planne-software/uni/v2';

import { findCountryByAlpha3Code, getSortedCountriesExcludingBrazil } from '@src/common/auth';
import { useTranslate } from '@src/core/hooks';
import { FOREIGN_IDENTITY_TYPE, isBuyerForeign, resolveForeignIdentityType } from '@src/domains/customers';

import { FieldSpan, InlineOptionsRow } from '../styles';
import type { SignupFormValues } from '../hooks/useSignupForm/signupSchema';

type ForeignIdentityFieldsProps = {
  control: Control<SignupFormValues>;
  countryCode: string;
  onCountryCodeChange: (countryCode: string) => void;
  disabled?: boolean;
};

export const ForeignIdentityFields = (props: ForeignIdentityFieldsProps) => {
  const { control, countryCode, onCountryCodeChange, disabled } = props;
  const { i18n } = useTranslate();

  const options = useMemo<SelectOption[]>(
    () => getSortedCountriesExcludingBrazil().map((country) => ({ label: country.name, value: country.alpha3 })),
    [],
  );
  const selected = options.find((option) => option.value === countryCode);

  const renderCountryOption = useCallback(
    (_element: unknown, option: SelectOption) => (
      <ListItem
        onClick={() => onCountryCodeChange(option.value)}
        label={option.label}
        startSlot={() => <Flag code={findCountryByAlpha3Code(option.value)?.alpha2 ?? ''} alt={option.label} />}
      />
    ),
    [onCountryCodeChange],
  );

  // Mercosur nationalities may present a national id; everyone else can only use a passport.
  const showNationalDocument = resolveForeignIdentityType(countryCode) === FOREIGN_IDENTITY_TYPE.IDENTITY;
  const showPassport = isBuyerForeign(countryCode);

  return (
    <>
      <Controller
        name='countryCode'
        control={control}
        render={({ fieldState }) => (
          <Select<SelectOption>
            label={i18n('checkout_auth_signup_nationality_label')}
            options={options}
            value={selected}
            getOptionLabel={(option) => option.label}
            onChange={(_event, option) => onCountryCodeChange((option as SelectOption | null)?.value ?? '')}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            disabled={disabled}
            required
            size='medium'
            fullWidth
            preview={
              selected ? (
                <Stack direction='row' spacing={2} alignItems='center'>
                  <Flag code={findCountryByAlpha3Code(selected.value)?.alpha2 ?? ''} alt={selected.label} />
                  <Typography sx={{ font: 'inherit', color: uni('fg-neutral-action-default-rest') }}>
                    {selected.label}
                  </Typography>
                </Stack>
              ) : null
            }
            renderOption={renderCountryOption}
          />
        )}
      />

      <Controller
        name='identity'
        control={control}
        render={({ field: { onChange, onBlur, value, name, ref }, fieldState }) => (
          <Input
            type='text'
            name={name}
            id={name}
            label={i18n('checkout_auth_signup_foreign_document_label')}
            value={value ?? ''}
            onChange={onChange}
            onBlur={onBlur}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            disabled={disabled}
            required
            ref={ref}
            size='medium'
            fullWidth
          />
        )}
      />

      <FieldSpan>
        <Controller
          name='foreignIdentityType'
          control={control}
          render={({ field: { onChange, value } }) => (
            <InlineOptionsRow role='radiogroup' aria-label={i18n('checkout_auth_signup_foreign_document_type_label')}>
              {showNationalDocument ? (
                <FormControlLabel
                  control={
                    <Radio
                      name='foreignIdentityType'
                      value={FOREIGN_IDENTITY_TYPE.IDENTITY}
                      checked={value === FOREIGN_IDENTITY_TYPE.IDENTITY}
                      disabled={disabled}
                      onChange={() => onChange(FOREIGN_IDENTITY_TYPE.IDENTITY)}
                    />
                  }
                  label={i18n('checkout_auth_signup_foreign_document_identity')}
                />
              ) : null}

              {showPassport ? (
                <FormControlLabel
                  control={
                    <Radio
                      name='foreignIdentityType'
                      value={FOREIGN_IDENTITY_TYPE.PASSPORT}
                      checked={value === FOREIGN_IDENTITY_TYPE.PASSPORT}
                      disabled={disabled}
                      onChange={() => onChange(FOREIGN_IDENTITY_TYPE.PASSPORT)}
                    />
                  }
                  label={i18n('checkout_auth_signup_foreign_document_passport')}
                />
              ) : null}
            </InlineOptionsRow>
          )}
        />
      </FieldSpan>
    </>
  );
};

export default ForeignIdentityFields;
